import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import s from "./index.module.css";
import { GetIco } from "@/utils/icons";

function convertTime(timestamp) {
  let hours = Math.floor(timestamp / 60 / 60);
  let minutes = Math.floor(timestamp / 60) - hours * 60;
  let seconds = Math.floor(timestamp % 60);
  if (seconds < 10) seconds = "0" + seconds;
  let result = "";

  if (hours > 0) result += hours + ":";
  result += minutes + ":";
  result += seconds;

  return result;
}

const waveformBars = [12, 18, 8, 22, 16, 28, 10, 24, 18, 34, 14, 26, 20, 30, 12, 22, 16, 28, 10, 24, 18, 32, 14, 26, 20, 30, 12, 22, 16, 28];
const DURATION_SEEK_TIME = 24 * 60 * 60;

const getValidDuration = (value) => (
  Number.isFinite(value) && value > 0 ? value : 0
);

const AudioPlayer = ({ audioTrack, className, durationSec }) => {
  const [statusPlay, setStatus] = useState(false);
  const [dragFlag, setDragFlag] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(getValidDuration(Number(durationSec)));

  const audio = useRef();
  const trackTime = useRef();
  const audioPlay = useRef(null);
  const isResolvingDuration = useRef(false);

  const clearAudioInterval = () => {
    if (audioPlay.current) {
      clearInterval(audioPlay.current);
      audioPlay.current = null;
    }
  };

  const updateAudioProgress = (audioElement) => {
    const nextDuration = getValidDuration(audioElement.duration) || duration;
    const nextCurrentTime = Number.isFinite(audioElement.currentTime) ? audioElement.currentTime : 0;

    setCurrentTime(nextCurrentTime);
    if (nextDuration > 0) {
      setDuration(nextDuration);
    }
  };

  const Play = (e) => {
    e.stopPropagation()
    let audioI = audio.current;

    if (!audioI) return;

    if (isResolvingDuration.current) {
      isResolvingDuration.current = false;
      audioI.currentTime = 0;
      setCurrentTime(0);
    }

    audioI.play().then(() => {
      setStatus(true);
    }).catch(() => {
      setStatus(false);
    });
    clearAudioInterval();
    audioPlay.current = setInterval(() => {
      updateAudioProgress(audioI);
      if (audioI.ended) {
        setStatus(false);
        clearAudioInterval();
      }
    }, 100);
  };

  const Pause = (e) => {
    e.stopPropagation()
    let audioI = audio.current;
    setStatus(false);
    audioI?.pause();
    clearAudioInterval();
  };

  const setTrackerProgress = (e) => {
    e.stopPropagation()
    let audioI = audio.current;
    let audioLength = getValidDuration(audioI?.duration) || duration;

    if (!audioI || !audioLength) return;

    const rect = e.currentTarget.getBoundingClientRect();
    let offset = e.clientX - rect.left
    let itemWidth = rect.width
    let positionInPercent = (offset / itemWidth) * 100
    let positionOffset = audioLength * (positionInPercent / 100)
    if (!Number.isFinite(positionInPercent) || !e.currentTarget.getAttribute('data-audio-track')) {
      return
    }
    audioI.currentTime = positionOffset
    setCurrentTime(positionOffset);
  }

  useEffect(() => {
    isResolvingDuration.current = false;
    setStatus(false);
    setCurrentTime(0);
    setDuration(getValidDuration(Number(durationSec)));
    clearAudioInterval();
  }, [audioTrack, durationSec]);

  useEffect(() => {
    const audioElement = audio.current;
    if (!audioElement) return undefined;

    const resolveDuration = () => {
      const nextDuration = getValidDuration(audioElement.duration);

      if (nextDuration) {
        setDuration(nextDuration);

        if (isResolvingDuration.current) {
          isResolvingDuration.current = false;
          audioElement.currentTime = 0;
          setCurrentTime(0);
        }

        return true;
      }

      return false;
    };

    const handleLoadedMetadata = () => {
      if (resolveDuration()) return;
      if (duration > 0) return;

      if (!isResolvingDuration.current) {
        isResolvingDuration.current = true;
        try {
          audioElement.currentTime = DURATION_SEEK_TIME;
        } catch (error) {
          isResolvingDuration.current = false;
        }
      }
    };

    const handleTimeUpdate = () => {
      if (isResolvingDuration.current) {
        resolveDuration();
        return;
      }

      updateAudioProgress(audioElement);
    };

    const handleEnded = () => {
      setStatus(false);
      setCurrentTime(0);
      clearAudioInterval();
    };

    audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    audioElement.addEventListener('durationchange', resolveDuration);
    audioElement.addEventListener('timeupdate', handleTimeUpdate);
    audioElement.addEventListener('ended', handleEnded);

    return () => {
      audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audioElement.removeEventListener('durationchange', resolveDuration);
      audioElement.removeEventListener('timeupdate', handleTimeUpdate);
      audioElement.removeEventListener('ended', handleEnded);
      clearAudioInterval();
    };
  }, [audioTrack, duration]);

  const progress = duration > 0 ? Math.min((currentTime * 100) / duration, 100) : 0;
  const time = convertTime(statusPlay || currentTime > 0 ? currentTime : duration);
  const activeBarsCount = Math.ceil((progress / 100) * waveformBars.length);

  return (
    <div className={classNames(s.wrapper, className)}>
      {statusPlay ? (
        <button
          type="button"
          onClick={Pause}
          className={`${s.button} ${s.play}`}
          aria-label="Пауза"
          data-audio-play
        >
          <GetIco icon="pause" />
        </button>
      ) : (
        <button
          type="button"
          onClick={Play}
          className={`${s.button} ${s.play}`}
          aria-label="Воспроизвести"
          data-audio-play
        >
          <GetIco icon="play" />
        </button>
      )}
      <div
        ref={trackTime}
        onMouseDown={() => setDragFlag(true)}
        onMouseMove={(e) => dragFlag ? setTrackerProgress(e) : ''}
        onMouseUp={() => setDragFlag(false)}
        onClick={(e) => setTrackerProgress(e)}
        className={s.trackTime}
        style={{ "--value": `${progress}%` }}
        data-audio-track

      >
        <div className={s.waveform} aria-hidden="true">
          {waveformBars.map((height, index) => (
            <span
              key={`${height}-${index}`}
              className={classNames(s.waveformBar, index < activeBarsCount && s.waveformBarActive)}
              style={{ height }}
            />
          ))}
        </div>
        <audio ref={audio} src={audioTrack} className={s.audio} preload="metadata"></audio>
      </div>

      <span className={s.timer} data-audio-timer>
        {time}
      </span>
    </div>
  );
};

export default AudioPlayer;
